import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { forkJoin, lastValueFrom, take } from 'rxjs';
import store from 'store2';
import { ToastHelper } from '../../common/toaster.helper';
import { ClinicCalendarService } from 'src/app/services/clinic-calendar.service';
import {
  ClinicAppointmentTypeFormGroupService,
  ClinicAppointmentTypeFormGroupMapping
} from 'src/app/services/clinic-appointment-type-formgroup.service';
import {
  ClinicFormGroupDefaultService,
  ClinicFormGroup
} from 'src/app/services/clinic-form-group-default.service';
import { MenuPermissionFlatDto } from 'src/app/models/clinicParms/operation-item';

interface AppointmentTypeWithMapping {
  typeID: number;
  typeName: string;
  typeDesc: string;
  branchID: number;
  groupName?: string;
  mappingID?: number;
  requireForEachAppointment?: boolean;
  hasFormGroup: boolean;
}

@Component({
  selector: 'app-appointment-type-formgroup-setup',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './appointment-type-formgroup-setup.component.html',
  styleUrl: './appointment-type-formgroup-setup.component.scss'
})
export class AppointmentTypeFormgroupSetupComponent implements OnInit {
  branchID: number = 0;
  appointmentTypes: AppointmentTypeWithMapping[] = [];
  formGroups: string[] = [];
  isLoading: boolean = false;
  isSaving: boolean = false;

  // Modal properties
  showAssignModal: boolean = false;
  showDeleteModal: boolean = false;
  currentAppointmentType: AppointmentTypeWithMapping | null = null;
  selectedGroupName: string = '';
  selectedRequireForEachAppointment: boolean = false;

  // Pagination properties
  paginatedAppointmentTypes: AppointmentTypeWithMapping[] = [];
  currentPage: number = 1;
  pageSize: number = 10;
  totalPages: number = 0;

  // Filter properties
  filterText: string = '';
  showOnlyWithForms: boolean = false;

  constructor(
    private _toastr: ToastHelper,
    private _clinicCalendarService: ClinicCalendarService,
    private _appointmentTypeFormGroupService: ClinicAppointmentTypeFormGroupService,
    private _clinicFormGroupDefaultService: ClinicFormGroupDefaultService,
    private cdr: ChangeDetectorRef
  ) {}

  userpermissions: MenuPermissionFlatDto[] = JSON.parse(store('userPermissions') || '[]');
  ComponentName: string = 'Default Forms';
  ViewPermission: boolean = false;
  InsertPermission: boolean = false;
  DeletePermission: boolean = false;

  ngOnInit(): void {
    this.ViewPermission = this.userpermissions.find(u => u.cname == this.ComponentName && u.permissionName == 'View')? true : false;
    this.InsertPermission = this.userpermissions.find(u => u.cname == this.ComponentName && u.permissionName == 'Insert')? true : false;
    this.DeletePermission = this.userpermissions.find(u => u.cname == this.ComponentName && u.permissionName == 'Delete')? true : false;

    this.branchID = store('userBranches');
    this.loadData();
  }

  private async loadData(): Promise<void> {
    this.isLoading = true;
    try {
      const [appTypesRes, mappingsRes, formGroupsRes] = await lastValueFrom(
        forkJoin([
          this._clinicCalendarService.GetAllAppointmentTypes(this.branchID),
          this._appointmentTypeFormGroupService.getAllMappingsByBranch(this.branchID),
          this._clinicFormGroupDefaultService.GetClinicFormGroup(this.branchID)
        ])
      );
      this.processData(appTypesRes, mappingsRes, formGroupsRes);
    } catch (error) {
      console.error('Error loading data:', error);
      this._toastr.showError('Failed to load data');
      this.isLoading = false;
    }
  }

  private processData(appTypesRes: any, mappingsRes: any, formGroupsRes: ClinicFormGroup[]): void {
    try {
      // Parse appointment types
      const appTypes = JSON.parse(appTypesRes.data);

      // Parse mappings
      const mappings = mappingsRes as ClinicAppointmentTypeFormGroupMapping[];

      // Extract unique group names
      this.formGroups = [...new Set<string>(formGroupsRes.map(x => x.groupName))];

      // Merge appointment types with their mappings
      this.appointmentTypes = appTypes.map((appType: any) => {
        const mapping = mappings.find(m => m.typeID === appType.TypeID);
        return {
          typeID: appType.TypeID,
          typeName: appType.TypeName,
          typeDesc: appType.TypeDesc,
          branchID: appType.BranchID,
          groupName: mapping?.groupName,
          mappingID: mapping?.id,
          requireForEachAppointment: mapping?.requireForEachAppointment || false,
          hasFormGroup: !!mapping
        } as AppointmentTypeWithMapping;
      });

      this.initializePagination();
      this.isLoading = false;
      this.cdr.markForCheck();
    } catch (error) {
      console.error('Error processing data:', error);
      this._toastr.showError('Failed to process data');
      this.isLoading = false;
      this.cdr.markForCheck();
    }
  }

  // Filter and Pagination Methods
  get filteredAppointmentTypes(): AppointmentTypeWithMapping[] {
    let filtered = this.appointmentTypes;

    // Apply text filter
    if (this.filterText.trim()) {
      const searchTerm = this.filterText.toLowerCase();
      filtered = filtered.filter(at =>
        at.typeName.toLowerCase().includes(searchTerm) ||
        at.typeDesc?.toLowerCase().includes(searchTerm) ||
        at.groupName?.toLowerCase().includes(searchTerm)
      );
    }

    // Apply "only with forms" filter
    if (this.showOnlyWithForms) {
      filtered = filtered.filter(at => at.hasFormGroup);
    }

    return filtered;
  }

  private initializePagination(): void {
    this.currentPage = 1;
    this.updatePagination();
  }

  private updatePagination(): void {
    const filtered = this.filteredAppointmentTypes;
    this.totalPages = Math.ceil(filtered.length / this.pageSize);
    const startIndex = (this.currentPage - 1) * this.pageSize;
    const endIndex = startIndex + this.pageSize;
    this.paginatedAppointmentTypes = filtered.slice(startIndex, endIndex);
  }

  nextPage(): void {
    if (this.currentPage < this.totalPages) {
      this.currentPage++;
      this.updatePagination();
    }
  }

  previousPage(): void {
    if (this.currentPage > 1) {
      this.currentPage--;
      this.updatePagination();
    }
  }

  onFilterChange(): void {
    this.initializePagination();
  }

  // Modal Methods
  openAssignModal(appointmentType: AppointmentTypeWithMapping): void {
    this.currentAppointmentType = appointmentType;
    this.selectedGroupName = appointmentType.groupName || '';
    this.selectedRequireForEachAppointment = appointmentType.requireForEachAppointment || false;
    this.showAssignModal = true;
  }

  closeAssignModal(): void {
    this.showAssignModal = false;
    this.currentAppointmentType = null;
    this.selectedGroupName = '';
    this.selectedRequireForEachAppointment = false;
  }

  openDeleteModal(appointmentType: AppointmentTypeWithMapping): void {
    this.currentAppointmentType = appointmentType;
    this.showDeleteModal = true;
  }

  closeDeleteModal(): void {
    this.showDeleteModal = false;
    this.currentAppointmentType = null;
  }

  // Save and Delete Methods
  saveMapping(): void {
    if (!this.currentAppointmentType) return;

    if (!this.selectedGroupName.trim()) {
      this._toastr.showError('Please select a form group');
      return;
    }

    this.isSaving = true;

    const mapping: ClinicAppointmentTypeFormGroupMapping = {
      id: this.currentAppointmentType.mappingID || 0,
      typeID: this.currentAppointmentType.typeID,
      groupName: this.selectedGroupName,
      branchID: this.branchID,
      requireForEachAppointment: this.selectedRequireForEachAppointment,
      isActive: true,
      createdBy: store('userID'),
      updatedBy: store('userID')
    };

    this._appointmentTypeFormGroupService.saveFormGroupMapping(mapping)
      .pipe(take(1))
      .subscribe({
        next: () => {
          this.isSaving = false;
          this._toastr.showSuccess('Form group assigned successfully');
          this.closeAssignModal();
          this.loadData();
        },
        error: (err) => {
          this.isSaving = false;
          console.error('Error saving mapping:', err);
          this._toastr.showError('Failed to assign form group');
        }
      });
  }

  confirmDelete(): void {
    if (!this.currentAppointmentType) return;

    this.isSaving = true;

    this._appointmentTypeFormGroupService.removeFormGroupMapping(this.currentAppointmentType.typeID)
      .pipe(take(1))
      .subscribe({
        next: () => {
          this.isSaving = false;
          this._toastr.showSuccess('Form group removed successfully');
          this.closeDeleteModal();
          this.loadData();
        },
        error: (err) => {
          this.isSaving = false;
          console.error('Error removing mapping:', err);
          this._toastr.showError('Failed to remove form group');
        }
      });
  }

  // Utility Methods
  get Math() {
    return Math;
  }

  get hasFormGroups(): boolean {
    return this.formGroups && this.formGroups.length > 0;
  }
}
